import { Badge } from '@/components/ui/badge';
import { PickupVerificationBadge } from '@/components/reports/PickupVerificationBadge';
import { formatPickupInvestorRoute } from '@/components/reports/pickupInvestorDisplay';
import { cn } from '@/lib/utils';
import {
  countPickupPipelineFilters,
  pickupAttemptStatusLabel,
  pickupPipelineWorkState,
  pickupPipelineWorkStateLabel,
  summarizePickupPipeline,
  type PickupPipelineFilter,
  type PickupPipelineRow,
  type PickupPipelineWorkState
} from './pickupPipelineModel';
import {
  formatPickupDate,
  formatPickupCurrency,
  formatPickupExpiryShort,
  formatPickupPoints
} from './pickupPipelineFormatters';

interface PickupPipelineTableProps {
  rows: PickupPipelineRow[];
  allRows: PickupPipelineRow[];
  filter: PickupPipelineFilter;
  selectedId: string | null;
  nowMs?: number;
  onFilterChange: (filter: PickupPipelineFilter) => void;
  onSelect: (row: PickupPipelineRow) => void;
  onInspectVerification: (row: PickupPipelineRow, element: HTMLButtonElement) => void;
}

const FILTERS: Array<{ value: PickupPipelineFilter; label: string }> = [
  { value: 'open', label: 'Open' },
  { value: 'awaiting_answer', label: 'Awaiting answer' },
  { value: 'needs_request', label: 'Needs request' },
  { value: 'verification', label: 'Verification' },
  { value: 'resolved', label: 'Resolved' }
];

function statusClass(state: PickupPipelineWorkState, masterStatus: string): string {
  if (state === 'resolved' && masterStatus.startsWith('captured_')) {
    return 'border-success/30 bg-success/5 text-success-strong';
  }
  if (state === 'resolved') {
    return 'border-muted-foreground/30 bg-muted text-muted-foreground';
  }
  if (state === 'awaiting_answer') {
    return 'border-warning/40 bg-warning/5 text-warning-strong';
  }
  if (state === 'verification') {
    return 'border-destructive/30 bg-destructive/5 text-destructive';
  }
  return 'border-primary/30 bg-primary/5 text-primary';
}

export function PickupPipelineTable({
  rows,
  allRows,
  filter,
  selectedId,
  nowMs = Date.now(),
  onFilterChange,
  onSelect,
  onInspectVerification
}: PickupPipelineTableProps) {
  const counts = countPickupPipelineFilters(allRows);
  const summary = summarizePickupPipeline(allRows, nowMs);

  return (
    <aside
      data-pickup-pipeline-master
      aria-label="Pick Up work queue"
      className="flex min-h-0 min-w-0 flex-col overflow-hidden border-r bg-card"
    >
      <div className="shrink-0 border-b px-3 py-2.5">
        <div role="tablist" aria-label="Pipeline filters" className="flex flex-wrap gap-1">
          {FILTERS.map(({ value, label }) => {
            const active = filter === value;
            return (
              <button
                key={value}
                type="button"
                role="tab"
                aria-selected={active}
                className={cn(
                  'whitespace-nowrap rounded-full px-2.5 py-1 text-[10.5px] font-medium leading-[1.4] outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
                  active
                    ? 'bg-primary font-semibold text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                )}
                onClick={() => onFilterChange(value)}
              >
                {label} · {counts[value]}
              </button>
            );
          })}
        </div>
      </div>

      <div
        data-pickup-pipeline-list
        role="listbox"
        aria-label="Pick Up loans"
        className="min-h-0 flex-1 overflow-y-auto"
      >
        {rows.map((row) => {
          const selected = row.opportunity.id === selectedId;
          const workState = pickupPipelineWorkState(row);
          const statusLabel = pickupPipelineWorkStateLabel(workState);
          const statusDetail = row.opportunity.status === 'open'
            ? pickupAttemptStatusLabel(row.activeAttempt?.status)
            : statusLabel;
          const pickup = row.displayAttempt?.latest_observed_pickup_points
            ?? row.displayAttempt?.quoted_pickup_points
            ?? row.opportunity.latest_potential_points;
          const loanNumber = row.opportunity.los_loan_number || String(row.opportunity.ob_loan_id);
          const expiration = row.opportunity.latest_lock_expiration_at;
          const investorRoute = formatPickupInvestorRoute(
            row.opportunity.original_investor,
            row.displayAttempt?.investor_name
          );

          return (
            <div
              key={row.opportunity.id}
              role="option"
              aria-selected={selected}
              tabIndex={0}
              onClick={() => onSelect(row)}
              onKeyDown={(event) => {
                if (event.target === event.currentTarget
                  && (event.key === 'Enter' || event.key === ' ')) {
                  event.preventDefault();
                  onSelect(row);
                }
              }}
              className={cn(
                'cursor-pointer border-b border-l-[3px] border-l-transparent px-[13px] py-2.5 outline-none transition-colors hover:bg-muted/50 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
                selected && 'border-l-warning bg-muted/70 hover:bg-muted/80'
              )}
            >
              <div className="flex items-baseline justify-between gap-2">
                <span className="truncate text-[12.5px] font-semibold leading-none tabular-nums">
                  {loanNumber}
                </span>
                <span className={cn(
                  'shrink-0 text-[11.5px] font-bold leading-none tabular-nums',
                  pickup !== null && pickup > 0 ? 'text-success' : 'text-muted-foreground'
                )}>
                  {formatPickupPoints(pickup)}
                </span>
              </div>

              <div className="mt-1 flex items-baseline justify-between gap-2">
                <span className="truncate text-[11.5px] leading-none text-foreground/75" title={row.opportunity.borrower_name || ''}>
                  {row.opportunity.borrower_name || 'Borrower unavailable'}
                </span>
                <span
                  className={cn(
                    'shrink-0 text-[11px] font-semibold leading-none tabular-nums',
                    formatPickupExpiryShort(expiration, nowMs).includes('past')
                      ? 'text-destructive'
                      : 'text-muted-foreground'
                  )}
                  title={formatPickupDate(expiration)}
                >
                  {formatPickupExpiryShort(expiration, nowMs)}
                </span>
              </div>

              <div
                className="mt-1 truncate text-[10.5px] leading-none text-muted-foreground"
                title={investorRoute}
              >
                {investorRoute}
              </div>

              <div className="mt-1.5 flex min-h-6 flex-wrap items-center gap-1">
                <Badge
                  variant="outline"
                  title={statusDetail}
                  className={cn('h-5 whitespace-nowrap px-1.5 text-[9px]', statusClass(workState, row.opportunity.status))}
                >
                  {statusLabel}
                </Badge>
                <PickupVerificationBadge
                  value={{
                    status: row.cacheSchedule?.pickup_verification_status,
                    mismatchFields: row.cacheSchedule?.pickup_verification_mismatch_fields,
                    lastError: row.cacheSchedule?.pickup_verification_last_error,
                    retryAfter: row.cacheSchedule?.pickup_verification_retry_after,
                    lastVerifiedAt: row.cacheSchedule?.pickup_last_verified_at,
                    lockDurationDays: row.cacheSchedule?.pickup_verified_lock_duration_days,
                    obLockPeriodBucket: row.cacheSchedule?.pickup_verified_ob_lock_period_bucket
                  }}
                  target={{
                    encompassLoanId: row.opportunity.encompass_loan_id,
                    obLoanCacheId: row.opportunity.ob_loan_cache_id,
                    obLoanId: row.opportunity.ob_loan_id,
                    loanNumber: row.opportunity.los_loan_number,
                    borrowerName: row.opportunity.borrower_name
                  }}
                  onInspect={(element) => onInspectVerification(row, element)}
                />
              </div>
            </div>
          );
        })}
        {rows.length === 0 && (
          <div className="px-4 py-14 text-center text-sm text-muted-foreground">
            No Pick Up records match this view.
          </div>
        )}
        <div
          data-pickup-pipeline-footer
          className="space-y-1 px-3 py-3 text-center text-[10.5px] font-medium leading-tight text-muted-foreground tabular-nums"
        >
          <div>{rows.length} of {allRows.length} records · sorted by lock expiration</div>
          <div>Open potential {formatPickupCurrency(summary.openPotentialDollars)}</div>
          <div>
            Captured this month {formatPickupCurrency(summary.capturedThisMonthDollars)} claimed ·{' '}
            {formatPickupCurrency(summary.verifiedThisMonthDollars)} verified
          </div>
        </div>
      </div>
    </aside>
  );
}
